// Typed client for the Elysian Ledger API. The types in schema.ts are
// generated from api/openapi.yaml (`npm run gen:api`), so a contract change
// shows up as a compile error wherever the app uses the old shape.
import createClient, { type Middleware } from 'openapi-fetch';
import type { components, paths, operations } from './schema';

export type { components, paths, operations };
export type Schemas = components['schemas'];
export type Money = Schemas['Money'];
export type Problem = Schemas['Problem'];

export interface LedgerClientOptions {
  baseUrl: string;
  /** Returns the current bearer token (refreshed by the host app). */
  getToken: () => string | null | Promise<string | null>;
  /** Returns the organisation the user is working in. */
  getOrgId: () => string | null;
  fetch?: typeof fetch;
  /** Called when the API says the sign-in is no longer valid (401), so the app can ask again. */
  onUnauthorized?: () => void;
}

/** Thrown for any non-2xx response; carries the RFC 7807 problem body. */
export class LedgerApiError extends Error {
  readonly status: number;
  readonly problem: Problem;
  constructor(problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = 'LedgerApiError';
    this.status = problem.status;
    this.problem = problem;
  }
  /** True when the database refused the change on an accounting rule. */
  get isRuleViolation(): boolean {
    return this.status === 409;
  }
}

// Operations that post to the ledger. Each gets an Idempotency-Key so a
// retried request (flaky connection, double click) never posts twice.
const POSTING = /\/(post|void|send|reverse|approve|reconcile|commit|close|file)$|^\/journals$/;

/**
 * A random UUID (v4). crypto.randomUUID only exists on secure pages (HTTPS or localhost), so on
 * staging over plain HTTP posting failed (#5); crypto.getRandomValues works everywhere.
 */
export function randomUuid(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') {
    try { return c.randomUUID(); } catch { /* not a secure page: below */ }
  }
  const b = new Uint8Array(16);
  c.getRandomValues(b);
  b[6] = (b[6]! & 0x0f) | 0x40; // version 4
  b[8] = (b[8]! & 0x3f) | 0x80; // RFC 4122 variant
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export function idempotencyKey(): string {
  return randomUuid();
}

export function createLedgerClient(opts: LedgerClientOptions) {
  const base = opts.fetch ?? ((input: Request) => globalThis.fetch(input));
  // A failed connection becomes a plain-English error instead of "TypeError: Failed to fetch".
  const guarded = async (input: Request) => {
    try {
      return await base(input);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      throw new LedgerApiError({ title: 'Can’t reach the Ledger server. Check your internet connection and try again.', status: 0 });
    }
  };
  const client = createClient<paths>({ baseUrl: opts.baseUrl, fetch: guarded as typeof fetch });

  const auth: Middleware = {
    async onRequest({ request, schemaPath }) {
      const token = await opts.getToken();
      if (token) request.headers.set('Authorization', `Bearer ${token}`);
      const org = opts.getOrgId();
      if (org && !request.headers.has('Elysian-Org')) request.headers.set('Elysian-Org', org);
      if (request.method === 'POST' && POSTING.test(schemaPath) && !request.headers.has('Idempotency-Key')) {
        request.headers.set('Idempotency-Key', idempotencyKey());
      }
      return request;
    },
    async onResponse({ response }) {
      if (response.ok) return response;
      let problem: Problem;
      try {
        problem = (await response.clone().json()) as Problem;
      } catch {
        problem = { title: response.statusText || 'Request failed', status: response.status };
      }
      const status = problem.status ?? response.status;
      if (status === 401) {
        opts.onUnauthorized?.();
        problem = { ...problem, title: 'Your sign-in has ended. Please sign in again.', detail: undefined };
      }
      throw new LedgerApiError({ ...problem, status });
    },
  };
  client.use(auth);
  return client;
}

export type LedgerClient = ReturnType<typeof createLedgerClient>;

/** Unwraps an openapi-fetch result, so callers get data or an exception. */
export async function unwrap<T>(p: Promise<{ data?: T; error?: unknown }>): Promise<T> {
  const r = await p;
  if (r.error !== undefined) throw r.error instanceof Error ? r.error : new Error(String(r.error));
  return r.data as T;
}
